<template>
  <v-card>
    <v-card-title class="text-h5 font-weight-bold">
      COA Preview - {{ labResult.averoutN }}
      <v-spacer />
      <v-btn icon @click="$emit('close')">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-card-title>
    <v-divider />
    <v-card-text class="pa-0">
      <v-container fluid class="pa-4 pa-md-6">
        <v-row>
          <v-col cols="12" md="7">
            <v-card outlined class="pdf-preview-card">
              <v-card-title class="text-subtitle-1 font-weight-medium">
                PDF Preview
              </v-card-title>
              <v-divider />
              <v-card-text class="pa-0">
                <iframe
                  v-if="pdfPreviewUrl"
                  :src="pdfPreviewUrl"
                  class="pdf-iframe"
                ></iframe>
                <v-skeleton-loader v-else type="image" height="500" />
              </v-card-text>
            </v-card>
          </v-col>
          <v-col v-if="labResult.finalTest && !labResult.signed" cols="12" md="5">
            <v-card outlined class="signature-card">
              <v-card-title class="text-subtitle-1 font-weight-medium">
                Sign Off
              </v-card-title>
              <v-divider />
              <v-card-text>
                <!-- Header Section -->
                <div>
                  <div class="d-flex justify-space-between">
                    <span class="my-auto">{{ signatureHeader }}</span>
                  </div>
                </div>

                <!-- Signature Pad Section -->
                <div @click="openSignMode" class="d-flex justify-center signature-pad my-2">
                  <div v-if="signatureSvg" style="position: absolute; margin-top: -30px;">
                    <v-img
                      contain
                      :aspect-ratio="16 / 9"
                      max-height="100px"
                      max-width="200px"
                      :src="signatureSvg"
                    />
                  </div>
                  <div v-else class="text-center font-weight-thin my-auto mx-10">
                    Sign Here <v-icon>mdi-draw-pen</v-icon>
                  </div>
                </div>

                <!-- Footer Section -->
                <div>
                  <div class="d-flex justify-space-between">
                    <div v-if="signatureFooter" class="d-flex justify-space-between">
                      <span>( {{ signatureFooter }} )</span>
                    </div>
                    <span v-else>N/A</span>
                  </div>
                </div>

                <v-alert
                  v-if="signatureError"
                  type="error"
                  outlined
                  class="mt-4"
                >
                  Signature is required
                </v-alert>
              </v-card-text>
              <v-card-actions>
                <v-spacer />
                <v-btn color="primary" :loading="loading" @click="submitSignature">
                  Submit Signature
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-col>
          <v-col v-else-if="labResult.signed" cols="12" md="5">
            <v-card outlined class="signed-card">
              <v-card-title class="text-subtitle-1 font-weight-medium">
                Already Signed
              </v-card-title>
              <v-divider />
              <v-card-text class="text-center">
                <v-icon large color="green">mdi-check-circle</v-icon>
                <p class="mt-2">This lab result has been signed.</p>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </v-card-text>

    <!-- Signature Dialog -->
    <v-dialog
      v-resize="onResize"
      style="z-index: 1000"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      v-model="signMode"
      persistent
    >
      <v-card class="pt-5">
        <v-card-subtitle class="d-flex">
          <div class="my-auto">Sign Below</div>
          <v-spacer />
          <v-btn @click="closeDialog" icon>
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-subtitle>

        <v-card-text class="px-4">
          <v-sheet :elevation="2">
            <vue-signature
              :key="windowSize.x"
              v-if="loaded"
              :h="`${(windowSize.x / 3) < 200 ? 200 : windowSize.x / 3}px`"
              :w="`${windowSize.x * 0.8 < 300 ? 300 : windowSize.x * 0.8}px`"
              ref="signaturePad"
              :sigOption="option"
              :defaultUrl="tempSignatureSvg"
              @beginStroke="onBeginStroke"
            ></vue-signature>
          </v-sheet>
        </v-card-text>

        <v-card-actions class="d-flex justify-center">
          <v-btn class="ma-2" @click="saveSignature">
            <v-icon class="mr-2">mdi-content-save</v-icon>
            <span>Done</span>
          </v-btn>
          <v-btn class="ma-2" @click="clearSignaturePad">
            <v-icon class="mr-2">mdi-eraser</v-icon>
            <span>Clear</span>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import { mapActions } from "vuex";
import VueSignature from "vue-signature";

export default {
  components: { VueSignature },
  props: {
    labResult: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      newSignature: null,
      loading: false,
      pdfPreviewUrl: null,
      signatureError: false,
      signMode: false,
      signatureSvg: null, // Current signature displayed
      originalSignatureSvg: null, // Original signature before editing
      tempSignatureSvg: null, // Temporary signature during editing
      windowSize: {},
      option: {
        penColor: "rgb(0, 0, 0)",
        backgroundColor: "rgb(255,255,255)",
        onEnd: null,
      },
      loaded: false,
      signatureModified: false,
      signatureHeader: 'Lab Technician Signature',
      signatureFooter: '',
    };
  },
  async created() {
    try {
      const response = await this.$axios.get(`/inventory-lab-result/preview/pdf/${this.labResult.averoutN}`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      this.pdfPreviewUrl = URL.createObjectURL(blob);
    } catch (error) {
      console.error("Error fetching PDF:", error);
      this.addErrorMessage("Error fetching PDF");
    }
    this.signatureFooter = this.labResult.technician;
    this.option.onEnd = this.onEnd;
    this.windowSize = { x: window.innerWidth, y: window.innerHeight };
    this.loaded = true;
  },
  beforeDestroy() {
    if (this.pdfPreviewUrl) {
      URL.revokeObjectURL(this.pdfPreviewUrl);
    }
  },
  methods: {
    ...mapActions("messages", ["addErrorMessage", "addSuccessMessage"]),
    onBeginStroke() {
      this.signatureModified = true;
    },
    onEnd() {
      // Optional: Handle end of stroke if needed
    },
    clearSignaturePad() {
      if (this.$refs.signaturePad) {
        this.$refs.signaturePad.clear();
      }
      this.tempSignatureSvg = null;
      this.signatureModified = true;
    },
    openSignMode() {
      if (this.signatureSvg) {
        this.originalSignatureSvg = this.signatureSvg; // Save original
      } else {
        this.originalSignatureSvg = null;
      }
      this.tempSignatureSvg = this.signatureSvg; // Start with current signature
      this.signMode = true; // Open dialog
    },
    closeDialog() {
      this.signatureSvg = this.originalSignatureSvg; // Revert to original
      this.tempSignatureSvg = this.originalSignatureSvg;
      this.signMode = false; // Close dialog
      this.signatureModified = false;
    },
    saveSignature() {
      if (this.$refs.signaturePad) {
        const svg = this.$refs.signaturePad.save("image/svg+xml");

        if(!this.signatureModified){
          this.signatureSvg = this.originalSignatureSvg;
          this.newSignature = this.signatureSvg;
        }
        else if (svg && svg.trim() !== "") {
          this.signatureSvg = svg;
          this.tempSignatureSvg = svg;
          this.originalSignatureSvg = svg;
          this.newSignature = this.signatureSvg;
        } else {
          this.signatureSvg = null;
          this.tempSignatureSvg = null;
          this.originalSignatureSvg = null;
          this.newSignature = null;
        }
      }
      this.signMode = false; // Close dialog
      this.signatureModified = false;
    },
    async svgToPngFile(svgDataUrl, fileName) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);
          canvas.toBlob((blob) => {
            if (blob) {
              const file = new File([blob], fileName, { type: 'image/png' });
              resolve(file);
            } else {
              reject(new Error('Failed to create PNG blob'));
            }
          }, 'image/png');
        };
        img.onerror = (error) => {
          reject(error);
        };
        img.src = svgDataUrl;
      });
    },
    async submitSignature() {
      if (!this.newSignature) {
        this.signatureError = true;
        return;
      }
      this.signatureError = false;
      this.loading = true;
      try {
        const pngFile = await this.svgToPngFile(this.newSignature, `signature_${this.labResult.id}.png`);
        const formData = new FormData();
        formData.append('signature', pngFile);
        await this.$axios.post(`/inventory-lab-result/${this.labResult.id}/sign`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        this.addSuccessMessage("Signed successfully");
        this.$emit("signed");
        this.$emit("close");
      } catch (error) {
        this.addErrorMessage("Error signing: " + (error.response ? error.response.data : error.message));
      } finally {
        this.loading = false;
      }
    },
    onResize() {
      this.windowSize = { x: window.innerWidth, y: window.innerHeight };
    },
  },
};
</script>

<style scoped>
.pdf-preview-card,
.signature-card,
.signed-card {
  height: 100%;
}

.pdf-iframe {
  width: 100%;
  height: 60vh;
  min-height: 400px;
  border: none;
}

.signature-pad {
  width: 100%;
  max-height: 100px;
  min-width: 100px;
  min-height: 50px;
  border-bottom: 1px solid #ccc;
  cursor: pointer;
  position: relative; /* Ensure the signature image is positioned correctly */
}

@media (max-width: 960px) {
  .pdf-iframe {
    height: 50vh;
  }
}

.text-h5 {
  word-break: break-word;
}
</style>